// The ONE PAN scrub shared by the structured logger and the three Sentry scrubbers (#15086, S3). Pure and
// dependency-free, and it names no host global, so it is safe in the browser bundle and in the edge runtime (proof:
// apps/customer-apps/src/lib/security/pan-redaction-edge-safe.test.ts). Do not add an import.
//
// It is GATED, because a false positive in a log line or a Sentry event corrupts an id or an amount. A run is redacted
// only when all of these hold: 13-19 digits, digit groups joined by at most one space or hyphen (or its URL-encoded form,
// `%20` / `%2D`), leading digit 2-6 (every major network; excludes epoch-millisecond strings and CardSecure tokens, which
// start 9), not adjacent to a letter or digit (a 13-digit decimal run inside a 32-char hex trace id is Luhn-valid 10% of
// the time), and Luhn-valid.
//
// A PAN next to another number ("qty 2 4111...", "4111... 12") joins into one longer run that fails Luhn as a whole, so
// every contiguous sub-run of digit groups is tried too, longest first.
//
// URL-encoding (#15424): `%20` and `%2D` join groups like the space and hyphen they stand for. Any other escape that
// decodes to a character that is not an ASCII letter or digit (punctuation, a control character such as `%0A`, a byte of
// a UTF-8 character such as `%C2%A0`) is a delimiter, not part of the PAN; escapes of letters and digits (`%41`, `%34`)
// are not. How the digit run meets a delimiter escape depends on which of its two hex digits are decimal:
//   - Both letters or ending in a letter (`%3D`, `%0A`, `%2F`): the run starts after it, and its letter must not glue
//     the PAN to it (`card%3D4111...`).
//   - Both decimal (`%20`, `%22`, `%40`, `%09`): its two digits are swallowed into the first group of the run
//     (`Card%204111...` scans as the run `204111`).
//   - A letter then a decimal (`%A0`): its last digit is swallowed the same way.
// Swallowed digits are read back out. A `%` straight before a run reads two ways: the escape, or a raw percent sign
// before a PAN (SQL `LIKE '%4012...%'`). Both readings are applied and whatever either finds is redacted, so a number
// the scrub caught before this change is not missed because of it, with one deliberate exception: 20, 21, 28 and 29 are
// the escapes of space, `!`, `(` and `)` and start no current card network (only defunct ranges: enRoute 2014 and 2149,
// and the legacy 15-digit JCB 2131), so the raw reading never starts a PAN in them (an epoch after `%20` must not fuse
// into one). A legacy 2131 number straight after a raw `%` is therefore not redacted.
//
// Two shapes are not digit runs at all. Track-1 magnetic-stripe data (`%B` PAN `^` NAME `^` ... `?`) has its PAN glued to
// the `B`, and is redacted by its structure, with no Luhn gate. A PAN stored as a JSON NUMBER is not a string; see
// redactPanNumber.
//
// Out of scope by design (pinned in test/pan-redaction.test.ts): a PAN glued to letters (`ref4111...`), prefixes outside
// 2-6 (81/82 UnionPay), a form-encoded `+` separator, a double-encoded (`%2520`) or fully percent-encoded PAN,
// URL-encoded Track-1 data, and a 17-19 digit PAN stored as a JSON number (a double cannot hold it, so the digits are
// already rounded and Luhn means nothing). The boundary check is manual because a lookbehind regex throws at module
// load in older Safari.
export const PAN_REDACTION_PLACEHOLDER = '[redacted-pan]';
const MIN_PAN_DIGITS = 13;
const MAX_PAN_DIGITS = 19;
const MAX_PAN_GROUP_DIGITS = 6;
// A group separator: a space, a hyphen, or the URL-encoded form of either.
const GROUP_SEPARATOR_SOURCE = ' |-|%20|%2[Dd]';
const GROUP_SEPARATOR = new RegExp(GROUP_SEPARATOR_SOURCE);
const DIGIT_RUN = new RegExp(`\\d+(?:(?:${GROUP_SEPARATOR_SOURCE})\\d+)*`, 'g');
const HEX_PAIR = /^[0-9A-Fa-f]{2}$/;
// Prefixes that start no current card network, only defunct ranges (isPan would otherwise accept their leading 2).
// They are the escapes of space, `!`, `(` and `)`, so the raw reading of a run beginning with them after a `%` never
// starts a PAN in its first group; and they are what a `yyyymmddHHMMSS` or far-future epoch-millisecond number starts
// with, so a numeric leaf with one is not scrubbed (see redactPanNumber).
const NEVER_A_CARD_PREFIX = /^(?:20|21|28|29)/;
// Track-1: format code `B` (after a `%` start sentinel that readers often strip), PAN, `^`, then the name, expiry,
// service code and discretionary data up to the `?` end sentinel. A whole track is at most 79 characters, so the tail is
// bounded by that rather than by guessing the field widths: a name over 26 characters or a record cut off after the PAN's
// caret still goes, and the discretionary data (which can carry a CVV) never survives. When a `?` end sentinel follows
// within that span the record runs through it whatever the characters between (a reader may emit a mixed-case name).
// With no sentinel, the stripe's 6-bit alphabet is ASCII 0x20-0x5F (space, digits, upper-case letters, punctuation), so
// the tail stops at the first character outside it (a lower-case letter, a brace) instead of eating the log text that
// follows an unterminated record. Neither form crosses a line break, and both stop at `"` and `\`, the quote that
// closes a JSON string held inside a string and the escape backslash: a log line puts one straight after a record with
// no end sentinel, and no field of a real track holds them. `&` is deliberately
// NOT a stop: a name may contain one (`SMITH/JOHN & JANE`), stopping there would leave the expiry and a possible CVV
// behind it visible, and a raw track in a URL is implausible (its `^` is percent-encoded there). A bare `B` (no `%`)
// must not be glued to a letter or digit before it (`SKU AB...^2 units`); the `%` is its own boundary.
const TRACK1 = /%?B\d{13,19}\^(?:[^?\r\n"\\]{0,79}\?|[\x20\x21\x23-\x3E\x40-\x5B\x5D-\x5F]{0,79})/g;
const DEFAULT_MAX_DEPTH = 16;
/** Luhn checksum over a string of digits. False for an empty string or any non-digit. */
export function isLuhnValid(digits) {
    let sum = 0;
    let double = false;
    for (let i = digits.length - 1; i >= 0; i--) {
        let d = digits.charCodeAt(i) - 48;
        if (d < 0 || d > 9)
            return false;
        if (double) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
        double = !double;
    }
    return digits.length > 0 && sum % 10 === 0;
}
function isAlphanumericCode(c) {
    return (c >= 48 && c <= 57) || (c >= 65 && c <= 90) || (c >= 97 && c <= 122); // NaN (out of range) is false
}
function isPan(digits) {
    const lead = digits.charCodeAt(0);
    return lead >= 50 && lead <= 54 && isLuhnValid(digits);
}
// `run` is digit groups joined by single separators (a space, a hyphen, or `%20` / `%2D`). Find the PANs inside it: every
// contiguous range of groups holding 13-19 digits that is Luhn-valid, never touching a glued edge of the run. Windows
// may overlap and are all kept, because a longer window that starts inside a real PAN can be Luhn-valid by chance (a
// card number followed by an expiry and a CVV: about 3% of random tails), and choosing it over the PAN's own window
// would leave the PAN's leading group in the clear; redactSpans merges what overlaps. One pass per length keeps the
// work linear in the run. Returns each window's character span.
function panSpans(run, leftGlued, rightGlued, firstGroupExtra = 0) {
    const groups = run.split(GROUP_SEPARATOR);
    const g = groups.length;
    if (groups.reduce((sum, grp) => sum + grp.length, 0) < MIN_PAN_DIGITS)
        return [];
    const found = [];
    for (let len = MAX_PAN_DIGITS; len >= MIN_PAN_DIGITS; len--) {
        for (let i = leftGlued ? 1 : 0; i < g; i++) {
            // A PAN leads 2-6 (see isPan), so a start that cannot is skipped before anything is scanned or joined.
            const lead = groups[i].charCodeAt(0);
            if (lead < 50 || lead > 54)
                continue;
            let digits = 0;
            let end = i; // exclusive
            while (end < g && digits < len)
                digits += groups[end++].length;
            if (digits !== len || (rightGlued && end === g))
                continue;
            // A PAN written in groups uses groups of at most 6 digits (4-4-4-4, 4-6-4, 4-6-5). A longer group inside a
            // multi-group candidate is some other identifier (a UUID's 8-digit first group joined across hyphens, a
            // timestamp next to a short number), and redacting it would corrupt that id in every log line.
            // `firstGroupExtra` is the digits an escape reading stripped off the front of the run's first group: they still
            // count toward that group's size here (an 8-digit UUID group after `%40` is 6 digits once the escape is dropped).
            if (end - i > 1 &&
                groups
                    .slice(i, end)
                    .some((grp, k) => grp.length + (i + k === 0 ? firstGroupExtra : 0) > MAX_PAN_GROUP_DIGITS))
                continue;
            if (!isPan(groups.slice(i, end).join('')))
                continue;
            found.push([i, end - 1]);
        }
    }
    if (found.length === 0)
        return [];
    const starts = [];
    let at = 0;
    for (const grp of groups) {
        starts.push(at);
        at += grp.length + (run.charCodeAt(at + grp.length) === 37 ? 3 : 1); // 37 = '%', the start of `%20` / `%2D`
    }
    return found.map(([first, last]) => [starts[first], starts[last] + groups[last].length]);
}
// `run` with every span replaced by the placeholder. Spans that overlap (two readings of one run) merge into one.
function redactSpans(run, spans) {
    if (spans.length === 0)
        return run;
    const sorted = [...spans].sort((a, b) => a[0] - b[0]);
    let out = '';
    let cursor = 0;
    let [start, end] = sorted[0];
    for (const [s, e] of sorted.slice(1)) {
        if (s < end) {
            end = Math.max(end, e);
            continue;
        }
        out += run.slice(cursor, start) + PAN_REDACTION_PLACEHOLDER;
        cursor = end;
        [start, end] = [s, e];
    }
    return out + run.slice(cursor, start) + PAN_REDACTION_PLACEHOLDER + run.slice(end);
}
function scrubRun(run, leftGlued, rightGlued) {
    return redactSpans(run, panSpans(run, leftGlued, rightGlued));
}
// The character code of the `%XX` escape at `text[i]`, or -1 when no escape starts there.
function escapeCodeAt(text, i) {
    if (i < 0 || text.charCodeAt(i) !== 37)
        return -1; // 37 = '%'
    const hex = text.slice(i + 1, i + 3);
    return HEX_PAIR.test(hex) ? parseInt(hex, 16) : -1;
}
// A delimiter escape: one that decodes to anything but an ASCII letter or digit (see the header comment).
function isDelimiterCode(code) {
    return code >= 0 && !isAlphanumericCode(code);
}
// `run` starts with the last `lead` digits of a delimiter escape that the digit run swallowed (2 for `%20`, 1 for `%A0`).
// Reads the run both ways, as the header comment explains. The escape reading drops those digits, and the separator
// after them when they were the whole first group (`%20%204111...`), and scrubs what is left with the left edge open,
// because an escape precedes it. The raw reading scrubs the whole run, its left edge glued when the escape's digits
// cannot start a card. Whatever either reading finds is redacted: a reading cannot veto the other, because a chance
// Luhn-valid window that overlaps the real PAN (`22` + its first groups) would otherwise hide the rest.
function scrubAfterEscape(run, lead, rawLeftGlued, rightGlued) {
    const afterLead = run.charCodeAt(lead);
    const skip = lead + (afterLead === 37 ? 3 : afterLead === 32 || afterLead === 45 ? 1 : 0); // '%', ' ', '-'
    // When the escape's digits were only the front of the first group, the rest of that group keeps their length.
    const asEscape = panSpans(run.slice(skip), false, rightGlued, skip === lead ? lead : 0).map(([start, end]) => [start + skip, end + skip]);
    return redactSpans(run, [...panSpans(run, rawLeftGlued, rightGlued), ...asEscape]);
}
/**
 * Replace every Luhn-valid, PAN-shaped run in `text`, and every Track-1 magnetic-stripe record, with
 * {@link PAN_REDACTION_PLACEHOLDER}.
 */
export function redactLuhnPans(text) {
    if (text.length < MIN_PAN_DIGITS)
        return text;
    return text
        .replace(TRACK1, (record, offset, whole) => record.charCodeAt(0) !== 37 && isAlphanumericCode(whole.charCodeAt(offset - 1))
        ? record
        : PAN_REDACTION_PLACEHOLDER)
        .replace(DIGIT_RUN, (run, offset, whole) => {
        if (run.length < MIN_PAN_DIGITS)
            return run;
        const rightGlued = isAlphanumericCode(whole.charCodeAt(offset + run.length));
        const before = whole.charCodeAt(offset - 1);
        // A `%` straight before the run: the run's first two digits may be the escape's (`%20`, `%09`).
        if (before === 37) {
            return isDelimiterCode(escapeCodeAt(whole, offset - 1))
                ? scrubAfterEscape(run, 2, NEVER_A_CARD_PREFIX.test(run), rightGlued)
                : scrubRun(run, false, rightGlued);
        }
        // `%`, a hex letter, then the run (`%A0`, a UTF-8 byte): the run's first digit is the escape's. The raw reading
        // keeps the letter as glue, as it always did.
        if (isDelimiterCode(escapeCodeAt(whole, offset - 2)))
            return scrubAfterEscape(run, 1, true, rightGlued);
        // A delimiter escape ending in a hex letter right before the run (`%3D`, `%0A`): that letter is the escape's own
        // last digit, not a letter the PAN is glued to.
        return scrubRun(run, isAlphanumericCode(before) && !isDelimiterCode(escapeCodeAt(whole, offset - 3)), rightGlued);
    });
}
/**
 * A numeric leaf that IS a PAN: an integer of 13-16 digits, leading 2-6 but not 20, 21, 28 or 29, Luhn-valid (a card
 * number serialized as a JSON number, not a string). Returns {@link PAN_REDACTION_PLACEHOLDER} for it and the number
 * itself for anything else.
 */
export function redactPanNumber(n) {
    // The logger calls this for every number in every log line, so rule out the common case (a count, a duration, a
    // status) before allocating a string. 13 digits is at least 1e12. Above MAX_SAFE_INTEGER (16 digits leading 9, then
    // 17-19 digits) a double has already rounded the digits, so a Luhn check on them is a coin toss that would replace a
    // tenth of all such numbers at random; they are left alone (a documented limit). This also keeps an integer in
    // exponent form (1e21 and up) away from `isPan`.
    if (!Number.isInteger(n) || n < 1e12 || n > Number.MAX_SAFE_INTEGER)
        return n;
    const digits = String(n);
    // A number, unlike a string, is routinely a timestamp: a `yyyymmddHHMMSS` integer (CardPointe's `authdate`) is 14 digits
    // leading 20, and an epoch-millisecond count leads 20 from 2033. One in ten of them is Luhn-valid by chance, and the
    // replacement turns a number into a string. 20, 21, 28 and 29 start no current card network (see NEVER_A_CARD_PREFIX).
    if (NEVER_A_CARD_PREFIX.test(digits))
        return n;
    return isPan(digits) ? PAN_REDACTION_PLACEHOLDER : n;
}
// A plain object: its prototype is null or a realm's Object.prototype (whose own prototype is null). Realm-independent,
// so an object built in another realm (iframe, vm context) is still walked.
function isPlainObject(v) {
    const proto = Object.getPrototypeOf(v);
    return proto === null || Object.getPrototypeOf(proto) === null;
}
// Sentry stack-frame CODE-LOCATION fields (file paths, chunk names, module and function names, addresses). Rewriting a
// digit run there breaks source-map symbolication, so only these pass through; every other frame field (captured
// local `vars`, source context lines) is scrubbed like any data.
const FRAME_LOCATION_KEYS = new Set([
    'filename',
    'abs_path',
    'module',
    'function',
    'package',
    'lineno',
    'colno',
    'in_app',
    'platform',
    'instruction_addr',
    'symbol_addr',
    'image_addr',
    'addr_mode',
]);
function walkFrame(frame, depth, max, path) {
    if (frame === null ||
        typeof frame !== 'object' ||
        Array.isArray(frame) ||
        !isPlainObject(frame)) {
        return walk(frame, depth, max, path);
    }
    return Object.fromEntries(Object.entries(frame).map(([k, v]) => FRAME_LOCATION_KEYS.has(k) ? [k, v] : [redactLuhnPans(k), walk(v, depth + 1, max, path)]));
}
function walk(value, depth, max, path, parentKey = '') {
    if (typeof value === 'string')
        return redactLuhnPans(value);
    if (typeof value === 'number')
        return redactPanNumber(value);
    if (value === null || typeof value !== 'object')
        return value;
    if (depth >= max)
        return '[truncated]';
    if (path.has(value))
        return '[circular]';
    if (!Array.isArray(value) && !isPlainObject(value))
        return value;
    path.add(value);
    const out = Array.isArray(value)
        ? value.map((item) => walk(item, depth + 1, max, path, parentKey))
        : Object.fromEntries(Object.entries(value).map(([k, v]) => {
            // The SDK's own debug_meta (debug ids, image paths) sits on the event root only; a nested field that happens
            // to be named debug_meta is caller data and is scrubbed.
            if (k === 'debug_meta' && depth === 0)
                return [k, v];
            // Only a real Sentry stacktrace (`...stacktrace.frames`) gets the frame-location rule; any other field named
            // `frames` is caller data.
            if (k === 'frames' && parentKey === 'stacktrace' && Array.isArray(v)) {
                return [k, v.map((frame) => walkFrame(frame, depth + 2, max, path))];
            }
            return [redactLuhnPans(k), walk(v, depth + 1, max, path, k)];
        }));
    path.delete(value); // only the CURRENT path counts as a cycle; a shared reference is walked each time
    return out;
}
/**
 * A scrubbed COPY of `value`: every string (and object key) inside plain objects and arrays goes through
 * {@link redactLuhnPans}, and every number through {@link redactPanNumber}. Bounded (`maxDepth`, then '[truncated]'),
 * cycle-safe ('[circular]'), never mutates the input.
 * Non-plain objects (Date, class instances) are returned as-is.
 */
export function redactLuhnPansDeep(value, maxDepth = DEFAULT_MAX_DEPTH) {
    return walk(value, 0, maxDepth, new WeakSet());
}
//# sourceMappingURL=pan-redaction.js.map